import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import RootLayout from './components/RootLayout';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';

const HomePage = lazy(() => import('./pages/HomePage'));
const HubPage = lazy(() => import('./pages/HubPage'));
const VideoPage = lazy(() => import('./pages/VideoPage'));
const BlogListingPage = lazy(() => import('./pages/BlogListingPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const ToolPostPage = lazy(() => import('./pages/ToolPostPage'));
const ToolBlogListingPage = lazy(() => import('./pages/ToolBlogListingPage'));
const ToolBlogPostPage = lazy(() => import('./pages/ToolBlogPostPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const RequestRepairPage = lazy(() => import('./pages/RequestRepairPage'));
const SubmitVideoPage = lazy(() => import('./pages/SubmitVideoPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const SafetyDisclaimerPage = lazy(() => import('./pages/SafetyDisclaimerPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const AffiliateDisclosurePage = lazy(() => import('./pages/AffiliateDisclosurePage'));
const DMCATakedownPage = lazy(() => import('./pages/DMCATakedownPage'));
const AdvertisePartnershipsPage = lazy(() => import('./pages/AdvertisePartnershipsPage'));
const DoNotSellPage = lazy(() => import('./pages/DoNotSellPage'));
const PrivacyRequestPage = lazy(() => import('./pages/PrivacyRequestPage'));
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <HomePage />
          </Suspense>
        ),
      },
      {
        path: '/hub',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <HubPage />
          </Suspense>
        ),
      },
      {
        path: '/hub/:slug',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <HubPage />
          </Suspense>
        ),
      },
      {
        path: '/hub/:year/:make/:model/:component',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <HubPage />
          </Suspense>
        ),
      },
      {
        path: '/video/:id',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <VideoPage />
          </Suspense>
        ),
      },
      {
        path: '/blog',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <BlogListingPage />
          </Suspense>
        ),
      },
      {
        path: '/blog/:slug',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <BlogPostPage />
          </Suspense>
        ),
      },
      {
        path: '/tools',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ToolsPage />
          </Suspense>
        ),
      },
      {
        path: '/tools/blog',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ToolBlogListingPage />
          </Suspense>
        ),
      },
      {
        path: '/tools/blog/:slug',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ToolBlogPostPage />
          </Suspense>
        ),
      },
      {
        path: '/tools/:slug',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ToolPostPage />
          </Suspense>
        ),
      },
      {
        path: '/how-it-works',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <HowItWorksPage />
          </Suspense>
        ),
      },
      {
        path: '/about',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AboutPage />
          </Suspense>
        ),
      },
      {
        path: '/contact',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ContactPage />
          </Suspense>
        ),
      },
      {
        path: '/request-repair',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <RequestRepairPage />
          </Suspense>
        ),
      },
      {
        path: '/submit-video',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <SubmitVideoPage />
          </Suspense>
        ),
      },
      {
        path: '/terms',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <TermsOfServicePage />
          </Suspense>
        ),
      },
      {
        path: '/privacy',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PrivacyPolicyPage />
          </Suspense>
        ),
      },
      {
        path: '/safety',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <SafetyDisclaimerPage />
          </Suspense>
        ),
      },
      {
        path: '/cookie-policy',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CookiePolicyPage />
          </Suspense>
        ),
      },
      {
        path: '/affiliate-disclosure',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AffiliateDisclosurePage />
          </Suspense>
        ),
      },
      {
        path: '/dmca',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <DMCATakedownPage />
          </Suspense>
        ),
      },
      {
        path: '/advertise',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdvertisePartnershipsPage />
          </Suspense>
        ),
      },
      {
        path: '/do-not-sell',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <DoNotSellPage />
          </Suspense>
        ),
      },
      {
        path: '/privacy-request',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PrivacyRequestPage />
          </Suspense>
        ),
      },
      {
        path: '/admin/login',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminLoginPage />
          </Suspense>
        ),
      },
      {
        path: '/admin/*',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
]);
